// useFormInstance.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useEffect, useRef, useState } from 'react';
import { Form } from 'antd';
import { useModuleStore } from 'stores/module';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

// *******************************************************************************
// FUNCTIONS
// *******************************************************************************

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const useFormInstance = ({ data, watchFields = [] } = {}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { pageSettings: storedPageSettings } = useModuleStore();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const didSetInitialValues = useRef(false);
  const formRef = useCallback(node => {
    if (node !== null) setFormInstance(node);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [formInstance, setFormInstance] = useState(null);

  // Ref to track fields that should be excluded from auto-reset
  // Used when a field is cleared via allowClear and saved - prevents flash of old value
  const excludeFromResetRef = useRef(new Set());

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const formData = data || storedPageSettings?.doc;

    if (
      formData &&
      formInstance?.form?.setFieldsValue &&
      Object?.keys(formData)?.length
      //&& !didSetInitialValues.current
    ) {
      // If there are fields to exclude from reset, filter them out
      let dataToSet = formData;
      if (excludeFromResetRef.current.size > 0) {
        dataToSet = { ...formData };

        // For each excluded field path (e.g., 'fiscalData.defaultIcmsCst'), set it to null
        for (const fieldPath of excludeFromResetRef.current) {
          const parts = fieldPath.split('.');
          if (parts.length === 2) {
            const [parent, field] = parts;
            if (dataToSet[parent]) {
              dataToSet[parent] = { ...dataToSet[parent], [field]: null };
            }
          }
        }

        // Clear the exclusion set after applying
        excludeFromResetRef.current.clear();
      }

      formInstance.form.setFieldsValue(dataToSet);
      // didSetInitialValues.current = true;
    }
  }, [data, formInstance]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const form = formInstance?.form;

  const validateForm = async fields => {
    if (!form) return { valid: false, errors: null };

    try {
      if (Array.isArray(fields) && fields.length) {
        // Valida os campos diretamente
        await form.validateFields(fields);
      } else {
        await form.validateFields();
      }

      return { valid: true, errors: null };
    } catch (error) {
      const errorFields = error?.errorFields || [];

      // Reforça a exibição visual dos erros (borda + help) manualmente
      form.setFields(
        errorFields.map(({ name, errors }) => ({
          name,
          errors, // força renderização de erro no campo
        }))
      );

      return { valid: false, errors: errorFields };
    }
  };

  const watches = watchFields.reduce((acc, field) => {
    acc[field] = Form.useWatch(field, form);
    return acc;
  }, {});

  /**
   * Exclude fields from the next auto-reset (when data changes and triggers setFieldsValue)
   * Used when a field is cleared via allowClear and saved - prevents flash of old value
   * @param {string[]} fieldPaths - Array of field paths like ['fiscalData.defaultIcmsCst']
   */
  const excludeFieldsFromReset = useCallback((fieldPaths) => {
    if (Array.isArray(fieldPaths)) {
      for (const path of fieldPaths) {
        excludeFromResetRef.current.add(path);
      }
    }
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return {
    formRef,
    watches,
    validateForm,
    excludeFieldsFromReset,
    ...formInstance,
  };
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default useFormInstance;
